/**
 * Tool structured events — INPUTS to the renderer, never user-facing text.
 *
 * 2026-05-10 (Miles directive, Slack C0AMX0ACS9K/1778451390.755569):
 *   "this shouldn't be a hardcoded response (never should be). we shouldn't
 *    go down all the edge cases to avoid this issue anywhere in the account
 *    specific Prest0n ever"
 *
 * Tools that today emit hardcoded user-facing text/html via stream.finalize()
 * or helpers.addSystemMessage() are migrated to emit a ToolStructuredEvent
 * via the new `payload:` field. The post-tool renderer (renderStructuredEvent)
 * receives that payload + the last 6 conversation messages + a persona snippet
 * + the user's language, makes a single fast LLM call, and produces the
 * user-visible reply in the user's voice/language.
 *
 * Hard rules (enforced by scripts/forbid-hardcoded-user-strings.py):
 *   1. The payload MUST contain ALL underlying data — the renderer may
 *      choose any subset of it. The payload MUST NOT contain pre-rendered
 *      user-facing text/HTML.
 *   2. The optional `summary` field is a HINT to the renderer ("this is a
 *      list_roles result with 9 roles"). The renderer is free to ignore it.
 *   3. The discriminator is `kind: string` in dotted form, e.g.
 *      "rbac.my_permissions", "translate.complete". Loose typing is
 *      intentional: tool authors should not need to extend a closed union
 *      every time they add a structured event shape — but they MUST
 *      document the shape inline at the emit site.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Generic structured event interface. Tools emit one of these via
 * stream.finalize({ payload }) or helpers.addSystemMessage({ payload }).
 *
 * Use the typed `ToolStructuredEvent` union (below) when you want
 * exhaustive type narrowing in the renderer. New event kinds can be
 * added to that union as we migrate more tools — but the runtime
 * accepts any `kind: string` so unmigrated tools and tenant extensions
 * keep working.
 */
export interface ToolStructuredEventBase {
  /**
   * Dotted-path event discriminator, e.g. "rbac.list_roles",
   * "translate.complete", "tool.generic_error". Lowercase, snake_case
   * for the leaf segment.
   */
  kind: string;

  /**
   * The raw underlying data for the event. The renderer may use any
   * subset of these fields. MUST NOT contain pre-rendered user-facing
   * text/HTML — that is the renderer's job.
   */
  data: Record<string, any>;

  /**
   * Optional hint to the renderer summarising the event in a single
   * sentence. The renderer is free to ignore this. Useful when the
   * `data` shape is complex (e.g. nested arrays) and the LLM would
   * otherwise need to introspect deeply.
   *
   * Example: "list_roles result with 9 roles, 2 of them system-defined".
   */
  summary?: string;

  /**
   * Optional canonical tool ID that emitted the event. Used by the
   * renderer to scope the prompt ("you are rendering a result from the
   * RBAC tool"). Not used for routing.
   */
  toolId?: string;

  /**
   * Optional severity hint. "error" causes the renderer to compose a
   * recoverable error message; "warning" a soft warning; "success"
   * (default) a normal success message; "info" a neutral info message.
   */
  severity?: 'success' | 'info' | 'warning' | 'error';

  /**
   * Optional file attachments that should be passed through to the
   * final rendered message (PDF/DOCX downloads, etc.). The renderer
   * does NOT inspect these — they are forwarded verbatim.
   */
  attachments?: File[];
}

// ============================================================================
// Typed structured-event union (exhaustive narrowing for the renderer)
// ============================================================================

/** RBAC tool — get_my_permissions. */
export interface RbacMyPermissionsEvent extends ToolStructuredEventBase {
  kind: 'rbac.my_permissions';
  data: {
    permissions: string[];
    isOwner: boolean;
    rawResponse?: any;
  };
}

/** RBAC tool — list_members. */
export interface RbacListMembersEvent extends ToolStructuredEventBase {
  kind: 'rbac.list_members';
  data: {
    members: any[];
    rawResponse?: any;
  };
}

/** RBAC tool — list_roles. */
export interface RbacListRolesEvent extends ToolStructuredEventBase {
  kind: 'rbac.list_roles';
  data: {
    roles: any[];
    rawResponse?: any;
  };
}

/** RBAC tool — mutation completed (assign/revoke/create/update/delete). */
export interface RbacMutationEvent extends ToolStructuredEventBase {
  kind:
    | 'rbac.assign_role'
    | 'rbac.revoke_role'
    | 'rbac.create_role'
    | 'rbac.update_role'
    | 'rbac.delete_role';
  data: {
    operation: string;
    userId?: string;
    roleId?: string;
    roleIds?: string[];
    roleName?: string;
    rawResponse?: any;
  };
}

/** RBAC tool — operation failed. */
export interface RbacErrorEvent extends ToolStructuredEventBase {
  kind: 'rbac.error';
  severity: 'error';
  data: {
    operation: string;
    errorMessage: string;
  };
}

/** Translate tool — translation completed (with PDF/DOCX downloads). */
export interface TranslateCompleteEvent extends ToolStructuredEventBase {
  kind: 'translate.complete';
  data: {
    sourceLanguage?: string;
    targetLanguage: string;
    inputDocumentName?: string;
    outputFileNames?: string[];
    summary?: string;
  };
}

/** Translate tool — translation failed. */
export interface TranslateErrorEvent extends ToolStructuredEventBase {
  kind: 'translate.error';
  severity: 'error';
  data: { errorMessage: string };
}

/** Deep research tool — research completed. */
export interface DeepResearchCompleteEvent extends ToolStructuredEventBase {
  kind: 'deep_research.complete';
  data: {
    topic?: string;
    pdfFileName?: string;
    docxFileName?: string;
    summary?: string;
  };
}

/** Deep research tool — research failed. */
export interface DeepResearchErrorEvent extends ToolStructuredEventBase {
  kind: 'deep_research.error';
  severity: 'error';
  data: { errorMessage: string; phase?: string };
}

/** Video script tool — script completed. */
export interface VideoScriptCompleteEvent extends ToolStructuredEventBase {
  kind: 'video_script.complete';
  data: { topic?: string; pdfFileName?: string; summary?: string };
}

/** Video script tool — script failed. */
export interface VideoScriptErrorEvent extends ToolStructuredEventBase {
  kind: 'video_script.error';
  severity: 'error';
  data: { errorMessage: string };
}

/** Legal document creator — doc completed. */
export interface LegalDocCompleteEvent extends ToolStructuredEventBase {
  kind: 'legal_doc.complete';
  data: { docType?: string; pdfFileName?: string; summary?: string };
}

/** Legal document creator — failed. */
export interface LegalDocErrorEvent extends ToolStructuredEventBase {
  kind: 'legal_doc.error';
  severity: 'error';
  data: { errorMessage: string };
}

/** Multi-doc manifest tool — manifest completed/updated. */
export interface MultiDocManifestEvent extends ToolStructuredEventBase {
  kind: 'multi_doc_manifest.complete' | 'multi_doc_manifest.update';
  data: any;
}

/** Voice call tool. */
export interface VoiceCallEvent extends ToolStructuredEventBase {
  kind: 'voice_call.update' | 'voice_call.complete' | 'voice_call.error';
  data: any;
}

/** Document analyzer — completed. */
export interface DocumentAnalyzerEvent extends ToolStructuredEventBase {
  kind: 'document_analyzer.complete' | 'document_analyzer.error';
  data: any;
}

/** Composio action — completed. */
export interface ComposioActionEvent extends ToolStructuredEventBase {
  kind: 'composio.action_complete' | 'composio.action_error';
  data: any;
}

/** Payment gate. */
export interface PaymentGateEvent extends ToolStructuredEventBase {
  kind: 'payment.gate' | 'payment.complete' | 'payment.error';
  data: any;
}

/** Generic catch-all for tool-emitted events that don't fit a specific shape. */
export interface GenericToolEvent extends ToolStructuredEventBase {
  kind: 'tool.generic_success' | 'tool.generic_info' | 'tool.generic_warning' | 'tool.generic_error';
  data: any;
}

/**
 * Discriminated union of all known structured event shapes. The runtime
 * still accepts any `ToolStructuredEventBase` — this union exists for
 * exhaustive narrowing inside renderStructuredEvent and downstream
 * consumers that want type-safe access to data fields.
 */
export type ToolStructuredEvent =
  | RbacMyPermissionsEvent
  | RbacListMembersEvent
  | RbacListRolesEvent
  | RbacMutationEvent
  | RbacErrorEvent
  | TranslateCompleteEvent
  | TranslateErrorEvent
  | DeepResearchCompleteEvent
  | DeepResearchErrorEvent
  | VideoScriptCompleteEvent
  | VideoScriptErrorEvent
  | LegalDocCompleteEvent
  | LegalDocErrorEvent
  | MultiDocManifestEvent
  | VoiceCallEvent
  | DocumentAnalyzerEvent
  | ComposioActionEvent
  | PaymentGateEvent
  | GenericToolEvent
  // Fallback — any unknown kind is still accepted at runtime.
  | (ToolStructuredEventBase & { kind: string });

// ============================================================================
// Type guards
// ============================================================================

export function isToolStructuredEvent(value: unknown): value is ToolStructuredEventBase {
  if (!value || typeof value !== 'object') return false;
  const v = value as any;
  return typeof v.kind === 'string' && typeof v.data === 'object' && v.data !== null;
}
